import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  // SF Oct 25: cart changes are sent one at a time. Taps made while a change is still saving are
  // queued (latest quantity per item wins) instead of being silently ignored. The free-gift app
  // makes each save take ~2-3 s, and the theme used to block all taps during that time.
  #sfBusy = false;
  /** @type {Array<{line: number, quantity: number, action: string, key?: string, sfRetried?: boolean}>} */
  #sfQueue = [];
  /**
   * Cart lines returned by the last change while queued taps are being sent. Shopify can give a
   * line a new key when the cart changes (the ₹200 discount is re-split), so queued taps look
   * their line up again here before they are sent.
   * @type {Array<{key: string, variant_id: number}> | undefined}
   */
  #sfItems;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      key: event.target.closest?.('[data-key]')?.dataset.key || this.#sfRowKey(line),
    });
    const lineItemRow = this.#sfRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      key: this.#sfRowKey(line),
    });

    const cartItemRowToRemove = this.#sfRow(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.key] - The line item key, captured when the customer tapped.
   */
  updateQuantity(config) {
    if (this.#sfBusy) {
      const queued = this.#sfQueue.findIndex((item) => item.key && item.key === config.key);
      if (queued > -1) {
        this.#sfQueue[queued] = config;
      } else {
        this.#sfQueue.push(config);
      }
      return;
    }
    this.#sfBusy = true;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // SF Oct 23: change the exact line item (its key) instead of its position. The free-gift app can
    // add a gift line just before this request; Shopify puts new lines first, which shifted positions
    // and made + / - / remove change the wrong item. Falls back to the position if no key is found.
    const sfKey = this.#sfCurrentKey(config.key) || this.#sfRowKey(line);
    const body = JSON.stringify({
      ...(sfKey ? { id: sfKey } : { line: line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    let sfStatus = 0;

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        sfStatus = response.status;
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          await this.#sfHandleError(config, sfKey, sfStatus, parsedResponseText);
          return;
        }

        this.#sfItems = Array.isArray(parsedResponseText.items) ? parsedResponseText.items : undefined;

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        // SF Oct 25: if more taps are queued, keep the customer's latest numbers on screen; the
        // last queued change re-renders the cart when it finishes.
        if (!this.#sfQueue.length) {
          this.#sfFinishRemovals();
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full', { injectStylesheet: true });
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        // SF Oct 25: something went wrong; show the cart as it really is.
        return this.#sfRefresh();
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        this.#sfBusy = false;
        const next = this.#sfQueue.shift();
        if (next) {
          this.updateQuantity(next);
        } else {
          this.#sfItems = undefined;
        }
      });
  }

  /**
   * SF Oct 25: Shopify refused a change. If that was because the line's key had just changed, look
   * the line up again and retry once (setting a quantity twice is harmless). Otherwise show the cart
   * as it really is, then the reason under that item (e.g. "Only 2 items were added to your cart
   * due to availability.").
   * @param {{line: number, quantity: number, action: string, key?: string, sfRetried?: boolean}} config
   * @param {string | undefined} key - The key that was sent.
   * @param {number} status - The HTTP status.
   * @param {{errors?: unknown, description?: unknown}} response - The parsed response.
   */
  async #sfHandleError(config, key, status, response) {
    if (status === 400 && !config.sfRetried) {
      const cart = await fetch(`${Theme.routes.cart_url}.js`)
        .then((cartResponse) => cartResponse.json())
        .catch(() => null);

      if (cart && Array.isArray(cart.items)) {
        this.#sfItems = cart.items;
        if (!this.#sfQueue.some((item) => item.key && item.key === config.key)) {
          this.#sfQueue.unshift({ ...config, sfRetried: true });
        }
        return;
      }
    }

    await this.#sfRefresh();

    // Nothing to explain when an item was being removed.
    if (!config.quantity) return;

    const row = this.#sfRowFor(this.#sfCurrentKey(config.key) || key);
    const errorContainer = row?.querySelector('.cart-items__error');
    const errorText = row?.querySelector('.cart-item__error-text');
    if (!errorContainer || !errorText) return;

    const fallback = "Sorry, this item couldn't be updated. Please try again.";
    const reason = typeof response.errors === 'string' ? response.errors : response.description;
    errorText.textContent = status !== 400 && typeof reason === 'string' && reason ? reason : fallback;
    errorContainer.classList.remove('hidden');
  }

  /**
   * SF Oct 25: re-renders the cart from Shopify so quantities and totals match the real cart.
   */
  async #sfRefresh() {
    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, false);
      this.#sfFinishRemovals();
      await morphSection(this.sectionId, sectionHTML, 'full', { injectStylesheet: true });
    } catch (error) {
      console.error(error);
    }

    for (const input of this.querySelectorAll('cart-quantity-selector-component input')) {
      if (input instanceof HTMLInputElement) input.value = input.defaultValue;
    }

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * SF Oct 25: takes rows that are still animating out (after a remove tap) off the page right away,
   * just before the cart is re-drawn. If Shopify answered before the animation ended, the re-draw
   * could reuse the disappearing row for the next item, and that item then vanished from the list.
   */
  #sfFinishRemovals() {
    for (const row of this.querySelectorAll('tr.removing')) row.remove();
  }

  /**
   * SF Oct 25: the key to send for a tapped line. While queued taps are being sent, the line is
   * looked up again in the latest cart (by its variant) in case Shopify gave it a new key.
   * @param {string | undefined} key - The key captured when the customer tapped.
   * @returns {string | undefined}
   */
  #sfCurrentKey(key) {
    const items = this.#sfItems;
    if (!key || !items || items.some((item) => item.key === key)) return key;

    const variantId = key.split(':')[0];
    const matches = items.filter((item) => String(item.variant_id) === variantId);
    return matches.length === 1 ? matches[0].key : key;
  }

  /**
   * SF Oct 25: finds the row of cart line `line` from that line's own remove button, so it stays
   * correct even if another row was just removed from the screen.
   * @param {number} line - The line number (1-based) as rendered.
   * @returns {HTMLElement | undefined}
   */
  #sfRow(line) {
    const removeButton = this.querySelector(`[on\\:click="/onLineItemRemove/${line}"]`);
    const row = removeButton?.closest('[data-key]') || this.refs.cartItemRows?.[line - 1];
    return row instanceof HTMLElement ? row : undefined;
  }

  /**
   * SF Oct 25: the line item key of cart line `line`.
   * @param {number} line - The line number (1-based) as rendered.
   * @returns {string | undefined}
   */
  #sfRowKey(line) {
    return this.#sfRow(line)?.dataset.key;
  }

  /**
   * SF Oct 25: finds a row by its line item key, or by its variant if the key has changed.
   * @param {string | undefined} key - The line item key.
   * @returns {HTMLElement | undefined}
   */
  #sfRowFor(key) {
    if (!key) return undefined;
    const rows = Array.from(this.querySelectorAll('tr[data-key]')).filter((row) => row instanceof HTMLElement);
    const variantId = key.split(':')[0];
    return (
      rows.find((row) => row.dataset.key === key) ||
      rows.find((row) => row.dataset.key?.split(':')[0] === variantId)
    );
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#sfFinishRemovals();
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
